import { Fragment, useContext, useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useQueryClient } from "react-query";
import {
  Checkbox,
  FormControl,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

import CloseIcon from "assets/icons/close.svg?react";
import Button from "components/Button";
import DatePickerComponent from "components/DatePicker";
import HollowButton from "components/HollowButton";
import Loading from "components/Loading";
import SearchMemberInWorkspace from "components/SearchMemberInWorkspace";

import ModalsManagerContext from "contexts/ModalsManager";
import KeysContext from "contexts/KeysContext";

import casesService from "api/casesService";
import mailroomService from "api/mailroomService";
import parcelService, {
  IRequestCreateOutGoingParcel,
  Receiver,
} from "api/parcelService";
import generateLettersService, {
  EDocumentInternalStatus,
} from "api/generateLettersService";
import pluginService from "api/pluginService";
import usePlugin from "hooks/usePlugin";

import { EParcelType } from "dto/EParcelType";
import { EEntityType } from "dto/EEntityType";
import { ItemKind } from "dto/IKindItems";
import { ICaseParticipant } from "dto/Cases/ICaseParticipant";
import { IWorkspaceMember } from "dto/IWorkspace";
import { IParcelLookup } from "dto/Mailroom/IMailroom";
import { EParcelDeliveryCategory } from "dto/Mailroom/IDispatchChart";
import { useTranslation } from "react-i18next";

import { getUserId } from "utils/getUserId";
import { reEncrypt } from "utils/reEncrypt";
import { readAsBase64 } from "utils/readAsBase64";
import { ENotificationType, notification } from "utils/notification";
import { IPackage } from "dto/IPackage";
import { IHybridSettings, defaultHybridSettings } from "dto/IHybridSettings";
import PuhOptionsPanel from "components/PuhOptionsPanel";
import OwnSignFlow from "./OwnSignFlow";
import { attachEbiLetterKeys, letterPublicKeyFor } from "utils/ebiLetterKeys";
import { getErrorMessage } from "utils/getErrorMessage";
import { IError } from "dto/IError";

import styles from "./styles.module.scss";

// Kanały zewnętrzne — odbiorca poza Notą nie odszyfruje treści, więc wtyczka wgrywa PDF plaintext.
const PLAINTEXT_CHANNELS: EParcelType[] = [
  EParcelType.Email,
  EParcelType.ExternalEDelivery,
  EParcelType.Puh,
];

// Mapowanie formy doręczenia (słownik biura podawczego) na kanał wysyłki. Elektroniczne rozpoznajemy
// po nazwie pozycji: "hybryd"/"PUH" → PUH, "mail" → e-mail, "doręcz" → rządowe e-Doręczenia; reszta → papier.
const kindToParcel = (kind?: IParcelLookup): EParcelType => {
  if (!kind || !kind.IsElectronic) return EParcelType.TraditionalLetter;
  // EBI po kategorii formy — pismo zostaje zaszyfrowane (poza PLAINTEXT_CHANNELS)
  if (kind.Category === EParcelDeliveryCategory.Ebi) return EParcelType.Ebi;
  const n = (kind.Name ?? "").toLowerCase();
  if (n.includes("hybryd") || n.includes("puh")) return EParcelType.Puh;
  if (n.includes("mail")) return EParcelType.Email;
  if (n.includes("dor")) return EParcelType.ExternalEDelivery;
  return EParcelType.TraditionalLetter;
};

type SignMode = "analog" | "own" | "pass";

type StepKey = "recipients" | "cover" | "sign";

const STEP_LABELS: Record<StepKey, string> = {
  recipients: "Adresaci",
  cover: "Pismo przewodnie",
  sign: "Podpis",
};

interface DocRef {
  DocumentId: string;
  CaseId: string | null;
  EncryptionKeyId: string | null;
  Title: string | null;
}

const toDocRef = (raw: string | null): DocRef | null => {
  if (!raw) return null;
  const pack = JSON.parse(raw) as IPackage;
  return {
    DocumentId: pack.Id,
    CaseId: pack.Case?.Id ?? null,
    EncryptionKeyId: pack.EncryptionKeyId ?? null,
    Title: pack.S1 ?? null,
  };
};

// Forma doręczenia = pozycja słownika ParcelDeliveryKinds (List polecony / List ZPO /
// List zwykły / Doręczenie osobiste / Przesyłka kurierska ...), tak jak rodzaje przesyłek
// w Magistracie. W MVP wszystkie idą papierowo (Type=TraditionalLetter, wstrzymane w biurze
// podawczym) — DeliveryKindId niesie konkretną formę. Elektronika (realna wysyłka e-mail /
// e-Doręczenia) wymaga dołączenia odszyfrowanego PDF (model E2E) i dojdzie osobno.

const partKey = (p: ICaseParticipant) => String(p.ParticipantId ?? p.Id ?? "");

// EBI wymaga PESEL-u i e-maila adresata (BE wylicza bit Ebi w ParcelTypes uczestnika).
const hasEbiChannel = (p: ICaseParticipant) =>
  ((p.ParcelTypes ?? 0) & EParcelType.Ebi) !== 0;

const GenerateLettersModal = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const userId = getUserId(auth.user!);
  const { modals, setModals } = useContext(ModalsManagerContext);
  const { keys } = useContext(KeysContext);
  const plugin = usePlugin();

  const doc = useMemo<DocRef | null>(
    () => toDocRef(localStorage.getItem("package")),
    [],
  );

  const { data: prepareData } = generateLettersService.usePrepareLetters(
    doc?.DocumentId ?? "",
  );
  const prep = prepareData?.data;

  const { data: caseData, isLoading: caseLoading } = casesService.useGetCase(
    doc?.CaseId ?? "",
    null,
    !!doc?.CaseId,
  );
  const participants: ICaseParticipant[] = useMemo(
    () => caseData?.Participants ?? [],
    [caseData],
  );

  // Słownik form doręczenia (rodzaje przesyłki jak w Magistracie) — konfigurowalny per profil.
  const { data: deliveryKindsData, isLoading: kindsLoading } =
    mailroomService.useGetDeliveryKinds();
  const deliveryKinds: IParcelLookup[] = useMemo(
    () => deliveryKindsData?.data ?? [],
    [deliveryKindsData],
  );
  const defaultKindId = deliveryKinds[0]?.Id ?? 0;

  // Lista adresatów potrzebuje uczestników teczki i słownika form doręczenia (select w wierszu).
  const recipientsLoading = caseLoading || kindsLoading;

  const { mutate: createParcel } = parcelService.useCreateOutGoingParcel();
  const { mutate: passToSign } = generateLettersService.usePassToSign();

  const [step, setStep] = useState<StepKey>("recipients");

  // Wybór adresatów: klucz uczestnika → { receive, cc, deliveryKindId }
  const [selections, setSelections] = useState<
    Record<string, { receive: boolean; cc: boolean; deliveryKindId: number }>
  >({});
  // data lokalna (YYYY-MM-DD) — unikamy przesunięcia o dzień z toISOString (UTC)
  const [date, setDate] = useState<string>(
    new Date().toLocaleDateString("sv-SE"),
  );
  const [leadTitle, setLeadTitle] = useState("");
  const [leadContent, setLeadContent] = useState("");

  const [signMode, setSignMode] = useState<SignMode>("analog");
  const [signer, setSigner] = useState<IWorkspaceMember | null>(null);
  const [note, setNote] = useState("");

  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Payload przekazany do podprzepływu własnego podpisu (certyfikat → pozycja
  // znacznika na piśmie bazowym → pętla podpisu każdej kopii). Null = nieaktywny.
  const [ownSignGen, setOwnSignGen] =
    useState<IRequestCreateOutGoingParcel | null>(null);

  // Ustawienia PUH (hybrydowa) — jeden zestaw dla całej wysyłki.
  const [hybridSettings, setHybridSettings] = useState<IHybridSettings>(
    defaultHybridSettings(),
  );

  const queryClient = useQueryClient();
  const close = () => {
    // Listy „Projekty pism" / „Wychodzące pisma" + status pisma na Dysku.
    queryClient.invalidateQueries(["letters"]);
    if (doc?.DocumentId) queryClient.invalidateQueries(["outgoing.byDocument", doc.DocumentId]);
    setModals({ ...modals, generateLetters: false });
  };

  const sel = (p: ICaseParticipant) =>
    selections[partKey(p)] ?? {
      receive: false,
      cc: false,
      deliveryKindId: defaultKindId,
    };

  const updateSel = (
    p: ICaseParticipant,
    patch: Partial<{ receive: boolean; cc: boolean; deliveryKindId: number }>,
  ) =>
    setSelections((prev) => ({
      ...prev,
      [partKey(p)]: { ...sel(p), ...patch },
    }));

  const buildReceiver = (
    p: ICaseParticipant,
    deliveryKindId: number,
  ): Receiver => ({
    // Autorytatywne mapowanie uczestnika→odbiorcy: ParticipantId (int) albo UserId (guid);
    // p.Id to id wiersza-złączenia CaseParticipant, NIE identyfikator podmiotu (zob.
    // NewSearchEntitiesWithCase / CreateOutgoingParcelModal.getEntityId).
    Id: String(p.ParticipantId ?? p.UserId),
    Type: EEntityType.NotaParticipant,
    // Kanał wyprowadzony z formy doręczenia (papier / e-mail / rządowe e-Doręczenia / PUH).
    Parcel: kindToParcel(kindById.get(deliveryKindId)),
    DeliveryKindId: deliveryKindId || null,
    Email: p.Email,
    EDeliveryAddress: p.EDeliveryAddress,
  });

  const buildRecipients = () => {
    const receivers: Receiver[] = [];
    const cc: Receiver[] = [];
    participants.forEach((p) => {
      const s = sel(p);
      if (s.cc) cc.push(buildReceiver(p, s.deliveryKindId));
      else if (s.receive) receivers.push(buildReceiver(p, s.deliveryKindId));
    });
    return { receivers, cc };
  };

  const hasReceiver = participants.some((p) => sel(p).receive || sel(p).cc);

  const kindById = useMemo(
    () => new Map(deliveryKinds.map((k) => [k.Id, k])),
    [deliveryKinds],
  );

  // Czy wśród wybranych adresatów jest kanał PUH (hybrydowy) — decyduje o panelu opcji + HybridSettings.
  const hasPuh = () =>
    participants.some((p) => {
      const s = sel(p);
      return (
        (s.receive || s.cc) &&
        kindToParcel(kindById.get(s.deliveryKindId)) === EParcelType.Puh
      );
    });

  // Skład wychodzący NIE jest wybierany ręcznie — określa się automatycznie na podstawie
  // rodzaju przesyłki, tak jak w Magistracie: przesyłki elektroniczne (e-mail / ePUAP /
  // e-Doręczenia) trafiają do składu elektronicznego, papierowe — do papierowego. Numeracja
  // partii jest wspólna (jeden skład/major na wysyłkę), więc o rodzaju składu decyduje forma
  // adresatów: skład elektroniczny tylko gdy WSZYSCY wybrani adresaci mają formę elektroniczną,
  // w przeciwnym razie papierowy. Gdy właściwego składu brak, o wyborze decyduje BE
  // (resolveOutgoingStore → domyślnie papierowy wychodzący).
  const resolveStoreId = (): string | null => {
    const stores = prep?.Stores ?? [];
    const selected = participants.filter((p) => sel(p).receive || sel(p).cc);
    const allElectronic =
      selected.length > 0 &&
      selected.every((p) => !!kindById.get(sel(p).deliveryKindId)?.IsElectronic);
    const target = allElectronic
      ? stores.find((s) => s.IsElectronic)
      : stores.find((s) => !s.IsElectronic);
    return target?.Id ?? prep?.DefaultStoreId ?? null;
  };

  // Druga linia w wierszu adresata (mniejsza czcionka) — dane kontaktowe zależne od wybranej
  // formy doręczenia: e-mail → adres e-mail, e-Doręczenia → adres ADE (EDeliveryAddress),
  // formy papierowe → adres fizyczny. Rozróżnienie e-mail/e-Doręczenia po nazwie pozycji
  // słownika (jedyny dostępny sygnał obok IsElectronic).
  const contactLine = (p: ICaseParticipant, deliveryKindId: number): string => {
    const kind = kindById.get(deliveryKindId);
    const name = (kind?.Name ?? "").toLowerCase();
    if (kind?.IsElectronic) {
      if (name.includes("dor")) return p.EDeliveryAddress ?? "";
      if (name.includes("mail")) return p.Email ?? "";
      return p.Email ?? p.EDeliveryAddress ?? "";
    }
    return p.Address ?? "";
  };

  // Krok „Pismo przewodnie" pojawia się tylko, gdy któryś z wybranych adresatów ma formę
  // elektroniczną (e-mail / ePUAP / e-Doręczenia) — pismo przewodnie jest treścią wiadomości
  // elektronicznej; przy przesyłce papierowej nie ma zastosowania. EBI też nie: treścią przesyłki
  // jest samo pismo (PDF zaszyfrowany kluczem dokumentu), a jawnego tekstu BE dla EBI nie zapisuje.
  const hasElectronic = participants.some((p) => {
    const s = sel(p);
    const kind = kindById.get(s.deliveryKindId);
    return (
      (s.receive || s.cc) &&
      !!kind?.IsElectronic &&
      kind.Category !== EParcelDeliveryCategory.Ebi
    );
  });

  const steps: StepKey[] = hasElectronic
    ? ["recipients", "cover", "sign"]
    : ["recipients", "sign"];
  const stepIndex = Math.max(0, steps.indexOf(step));
  const isLastStep = stepIndex === steps.length - 1;

  const handleNext = () => {
    if (step === "recipients") {
      if (!hasReceiver) {
        setError("Wskaż co najmniej jednego adresata (Otrzymuje lub Do wiadomości).");
        return;
      }
      if (!deliveryKinds.length) {
        setError(
          "Brak zdefiniowanych form doręczenia — skonfiguruj je w Ustawieniach → Biuro podawcze.",
        );
        return;
      }
      const missingKind = participants.some((p) => {
        const s = sel(p);
        return (s.receive || s.cc) && !s.deliveryKindId;
      });
      if (missingKind) {
        setError("Wskaż formę doręczenia dla każdego adresata.");
        return;
      }
      const ebiWithoutPesel = participants.find((p) => {
        const s = sel(p);
        return (
          (s.receive || s.cc) &&
          kindById.get(s.deliveryKindId)?.Category === EParcelDeliveryCategory.Ebi &&
          !hasEbiChannel(p)
        );
      });
      if (ebiWithoutPesel) {
        setError(
          t("ebiOffice.letterReceiverWithoutPesel", {
            name: ebiWithoutPesel.Label ?? ebiWithoutPesel.Name ?? "",
          }),
        );
        return;
      }
    }
    setError("");
    setStep(steps[Math.min(stepIndex + 1, steps.length - 1)]);
  };

  const handleBack = () => setStep(steps[Math.max(stepIndex - 1, 0)]);

  // analog=true → podpis odręczny na wydruku: BE pomija wymóg podpisanego PDF.
  const baseGeneration = (analog: boolean): IRequestCreateOutGoingParcel => {
    const { receivers, cc } = buildRecipients();
    return {
      // Formy nieszyfrowane (papier/e-mail/e-Doręczenia) — bez kluczy odbiorców.
      PublicKey: null as unknown as string,
      PrivateKey: null as unknown as string,
      OriginalPrivateKey: null as unknown as string,
      DocumentId: doc!.DocumentId,
      CaseId: doc!.CaseId,
      Title: leadTitle || doc!.Title || "",
      Content: leadContent,
      RawContent: leadContent || null,
      Attachments: [],
      Receivers: receivers,
      Cc: cc,
      ViaMailroom: true,
      HybridSettings: hasPuh() ? hybridSettings : undefined,
      ParcelStoreId: resolveStoreId(),
      DocumentDate: new Date(date).toISOString(),
      Analog: analog,
    };
  };

  // Generuje przez wtyczkę sparametryzowany PDF per odbiorca i dopina jego URI do payloadu.
  // (a) BE liczy mapę zmiennych DOCVARIABLE, (b) wtyczka wypełnia docx + renderuje + szyfruje
  // (kluczem dokumentu — zachowany oryginał autora) + wgrywa, (c) URI trafia na odbiorcę.
  const receiverIdOf = (r: Receiver) => String(r.Id ?? "");

  const attachGeneratedPdfs = async (
    gen: IRequestCreateOutGoingParcel,
  ): Promise<boolean> => {
    const all: { r: Receiver; cc: boolean }[] = [
      ...gen.Receivers.map((r) => ({ r, cc: false })),
      ...gen.Cc.map((r) => ({ r, cc: true })),
    ];
    if (all.length === 0) return true;

    // Adresaci EBI: osobny klucz pisma [firma + petent] — wtyczka szyfruje nim PDF zamiast kluczem dokumentu.
    // Osobno od wtyczki: brak klucza firmy ma własny komunikat (jak w ścieżce „own”), a nie „sprawdź wtyczkę”.
    try {
      await attachEbiLetterKeys(all.map(({ r }) => r));
    } catch (e) {
      setError(getErrorMessage(e as IError) || t("ebiOffice.letterKeysFailed"));
      return false;
    }

    try {
      // (a) BE: mapy zmiennych per odbiorca
      const prepRes = await generateLettersService.prepareContent({
        DocumentId: gen.DocumentId!,
        DocumentDate: gen.DocumentDate,
        Receivers: all.map(({ r, cc }) => ({
          ReceiverId: receiverIdOf(r),
          Id: r.Id ?? null,
          Type: (r.Type as number) ?? 0,
          Email: r.Email,
          Cc: cc,
        })),
      });
      const byId = new Map(
        (prepRes.data.Receivers ?? []).map((p) => [p.ReceiverId, p]),
      );

      // (b) Wtyczka: render + szyfrowanie + upload per odbiorca
      await plugin.ensureConnected();
      const genRes = await pluginService.generateLetters({
        DocumentId: gen.DocumentId!,
        Kind: ItemKind.EditorDocument,
        Receivers: all.map(({ r }) => {
          const p = byId.get(receiverIdOf(r));
          return {
            ReceiverId: receiverIdOf(r),
            // pusty = klucz dokumentu (zachowany oryginał autora); adresat EBI — klucz jego pisma
            PublicKey: letterPublicKeyFor(r),
            Variables: p?.Variables ?? {},
            RemoveParagraphsForVariables: p?.RemoveParagraphsForVariables ?? [],
            // Kanały zewnętrzne (e-mail / rządowe e-Doręczenia / PUH) — wtyczka wgrywa PDF plaintext.
            Plaintext: PLAINTEXT_CHANNELS.includes(r.Parcel),
          };
        }),
      });
      const uriById = new Map(
        (genRes.data.Results ?? [])
          .filter((x) => x.Success)
          .map((x) => [x.ReceiverId, x.Uri]),
      );

      // (c) Dopięcie URI wygenerowanych PDF do odbiorców
      let allOk = true;
      all.forEach(({ r }) => {
        const uri = uriById.get(receiverIdOf(r));
        if (uri) r.Uri = uri;
        else allOk = false;
      });

      if (!allOk) {
        setError(
          "Nie udało się wygenerować PDF dla wszystkich adresatów (sprawdź, czy wtyczka Nota jest uruchomiona).",
        );
        return false;
      }
      return true;
    } catch {
      setError(
        "Nie udało się wygenerować pism przez wtyczkę Nota. Upewnij się, że wtyczka jest uruchomiona i połączona.",
      );
      return false;
    }
  };

  const finish = async () => {
    if (!doc) return;
    setError("");

    // Podpis tradycyjny (odręczny na wydruku): generuje pisma od razu, ale NIE trafiają jeszcze
    // do biura podawczego — czekają w „Projektach pism" (ReadyToSign, przesyłki Draft) na wydruk,
    // podpis odręczny i „Przekaż do wysyłki" (jak w Magistracie).
    if (signMode === "analog") {
      setIsLoading(true);
      try {
        const gen = baseGeneration(true);
        if (!(await attachGeneratedPdfs(gen))) {
          setIsLoading(false);
          return;
        }
        await new Promise<void>((resolve, reject) =>
          createParcel(gen, { onSuccess: () => resolve(), onError: (e) => reject(e) }),
        );
        setIsLoading(false);
        notification({
          type: ENotificationType.SUCCESS,
          title: "Wygenerowano pisma",
          text: "Pisma czekają w „Projektach pism” — wydrukuj, podpisz i kliknij „Przekaż do wysyłki”.",
        });
        close();
      } catch {
        setIsLoading(false);
        notification({ type: ENotificationType.ERROR, title: "Błąd", text: "Nie udało się wygenerować pism." });
      }
      return;
    }

    // Własny podpis: KAŻDA kopia podpisywana osobno. Otwieramy podprzepływ
    // (certyfikat → pozycja znacznika na piśmie bazowym → pętla podpisu). Sama
    // generacja+podpis dzieje się w OwnSignFlow; createParcel odpala się po podpisie.
    if (signMode === "own") {
      setError("");
      const gen = baseGeneration(false);
      try {
        // Adresaci EBI: osobny klucz pisma [firma + petent] — OwnSignFlow przekaże go wtyczce.
        await attachEbiLetterKeys([...gen.Receivers, ...gen.Cc]);
      } catch (e) {
        setError(getErrorMessage(e as IError) || t("ebiOffice.letterKeysFailed"));
        return;
      }
      setOwnSignGen(gen);
      return;
    }

    // Przekaż do podpisu: ObjectPass (re-wrap klucza dokumentu) + zapis adresatów.
    if (signMode === "pass") {
      if (!signer) {
        setError("Wskaż osobę podpisującą.");
        return;
      }
      // Po podpisie przesyłki dostają dokument zaszyfrowany wspólnym kluczem dokumentu — tego klucza nie wolno
      // udostępnić petentowi EBI (BE też odrzuca takie przekazanie).
      const { receivers, cc } = buildRecipients();
      if ([...receivers, ...cc].some((r) => r.Parcel === EParcelType.Ebi)) {
        setError(t("ebiOffice.letterPassToSignBlocked"));
        return;
      }
      // Dla dokumentu szyfrowanego MUSIMY przekazać klucz podpisującemu — inaczej nie
      // odszyfruje dokumentu i nie podpisze. Jeśli klucza nie ma w KeysContext, przerywamy
      // (zamiast po cichu wysłać ReWrappedDocumentKey=null).
      const encryptionKey = doc.EncryptionKeyId
        ? keys.find((k) => k?.Id === doc.EncryptionKeyId)
        : undefined;
      if (doc.EncryptionKeyId && !encryptionKey) {
        setError(
          "Nie znaleziono klucza dokumentu (spróbuj ponownie za chwilę). Nie można przekazać do podpisu.",
        );
        return;
      }

      setIsLoading(true);
      try {
        let reWrappedDocumentKey: string | null = null;
        if (encryptionKey) {
          const uint = await reEncrypt(userId!, signer.UserId, encryptionKey);
          reWrappedDocumentKey = await readAsBase64(uint);
        }

        await new Promise<void>((resolve, reject) => {
          passToSign(
            {
              SignerUserId: signer.UserId,
              Note: note || null,
              DocumentEncryptionKeyId: doc.EncryptionKeyId,
              ReWrappedDocumentKey: reWrappedDocumentKey,
              Generation: baseGeneration(false),
            },
            { onSuccess: () => resolve(), onError: (e) => reject(e) },
          );
        });

        setIsLoading(false);
        notification({
          type: ENotificationType.SUCCESS,
          title: "Przekazano do podpisu",
          text: "Pismo trafiło na listę Do podpisu wskazanej osoby.",
        });
        close();
      } catch (e) {
        setIsLoading(false);
        notification({
          type: ENotificationType.ERROR,
          title: "Błąd",
          text: (e as Error)?.message ?? "Nie udało się przekazać do podpisu.",
        });
      }
    }
  };

  // Domknięcie własnego podpisu: OwnSignFlow zwrócił payload z podpisanymi URI
  // odbiorców (+ ReceiversPreSigned). Tworzymy przesyłki (wstrzymane w biurze podawczym).
  const finishOwnSign = async (signedGen: IRequestCreateOutGoingParcel) => {
    setIsLoading(true);
    try {
      await new Promise<void>((resolve, reject) =>
        createParcel(signedGen, {
          onSuccess: () => resolve(),
          onError: (e) => reject(e),
        }),
      );
      setIsLoading(false);
      setOwnSignGen(null);
      notification({
        type: ENotificationType.SUCCESS,
        title: "Wygenerowano i podpisano pisma",
        text: "Podpisane pisma trafiły do biura podawczego (do wysyłki).",
      });
      close();
    } catch {
      setIsLoading(false);
      setOwnSignGen(null);
      notification({
        type: ENotificationType.ERROR,
        title: "Błąd",
        text: "Pisma podpisano, ale nie udało się utworzyć przesyłek. Spróbuj ponownie.",
      });
    }
  };

  const statusHint = () => {
    switch (prep?.InternalStatus) {
      case EDocumentInternalStatus.ReadyToSign:
        return "Pismo jest do podpisu tradycyjnego (wydruk).";
      case EDocumentInternalStatus.ReadyToDigitalSign:
        return "Pismo przekazano do podpisu elektronicznego.";
      case EDocumentInternalStatus.ReadyToDispatch:
        return "Pismo jest gotowe do wysyłki (biuro podawcze).";
      default:
        return null;
    }
  };

  return (
    <>
    <div className={styles.overlay} onMouseDown={close}>
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.title}>
          <div>
            Generuj pisma
            {doc?.Title && <div className={styles.subtitle}>{doc.Title}</div>}
          </div>
          <CloseIcon className={styles.closeIcon} onClick={close} />
        </div>

        <div className={styles.steps}>
          {steps.map((sk, i) => (
            <Fragment key={sk}>
              {i > 0 && <span>›</span>}
              <span className={step === sk ? styles.stepActive : ""}>
                {i + 1}. {STEP_LABELS[sk]}
              </span>
            </Fragment>
          ))}
        </div>

        <div className={styles.content}>
          {statusHint() && <div className={styles.hint}>{statusHint()}</div>}

          {step === "recipients" && (
            <>
              <div className={styles.dateField}>
                <label className={styles.dateLabel}>Data pisma</label>
                <DatePickerComponent
                  date={new Date(date)}
                  setDate={(d) => setDate(d.toLocaleDateString("sv-SE"))}
                />
              </div>

              <div className={styles.sectionHeader}>Adresaci (uczestnicy teczki)</div>
              {recipientsLoading ? (
                <div className={styles.recipientsLoading}>
                  <Loading />
                </div>
              ) : participants.length === 0 ? (
                <div className={styles.empty}>
                  Brak uczestników w teczce. Dodaj adresatów do teczki, aby wygenerować pisma.
                </div>
              ) : (
                <div className={styles.recipientList}>
                  {participants.map((p) => {
                    const s = sel(p);
                    return (
                      <div key={partKey(p)} className={styles.recipient}>
                        <div className={styles.rInfo}>
                          <div className={styles.rName}>{p.Label ?? p.Name ?? "—"}</div>
                          {contactLine(p, s.deliveryKindId) && (
                            <div className={styles.rSub}>
                              {contactLine(p, s.deliveryKindId)}
                            </div>
                          )}
                        </div>
                        <label className={styles.check}>
                          <Checkbox
                            size="small"
                            checked={s.receive}
                            onChange={(e) => updateSel(p, { receive: e.target.checked, cc: e.target.checked ? false : s.cc })}
                          />
                          Otrzymuje
                        </label>
                        <label className={styles.check}>
                          <Checkbox
                            size="small"
                            checked={s.cc}
                            onChange={(e) => updateSel(p, { cc: e.target.checked, receive: e.target.checked ? false : s.receive })}
                          />
                          Do wiadomości
                        </label>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={deliveryKinds.length ? s.deliveryKindId : ""}
                            onChange={(e) =>
                              updateSel(p, { deliveryKindId: Number(e.target.value) })
                            }
                          >
                            {deliveryKinds
                              .filter(
                                (k) =>
                                  k.Category !== EParcelDeliveryCategory.Ebi ||
                                  hasEbiChannel(p) ||
                                  k.Id === s.deliveryKindId,
                              )
                              .map((k) => (
                                <MenuItem key={k.Id} value={k.Id}>
                                  {k.Name}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </div>
                    );
                  })}
                </div>
              )}

              {hasPuh() && (
                <PuhOptionsPanel
                  value={hybridSettings}
                  onChange={setHybridSettings}
                />
              )}
            </>
          )}

          {step === "cover" && (
            <>
              <div className={styles.sectionHeader}>Pismo przewodnie</div>
              <div className={styles.hint}>
                Treść wiadomości elektronicznej (e-mail / ePUAP / e-Doręczenia) towarzysząca
                pismu. Dla przesyłek papierowych pomijana.
              </div>
              <TextField
                size="small"
                label="Tytuł (opcjonalnie)"
                value={leadTitle}
                onChange={(e) => setLeadTitle(e.target.value)}
                fullWidth
              />
              <TextField
                size="small"
                label="Treść (opcjonalnie)"
                value={leadContent}
                onChange={(e) => setLeadContent(e.target.value)}
                multiline
                minRows={4}
                fullWidth
              />
            </>
          )}

          {step === "sign" && (
            <>
              <div className={styles.sectionHeader}>Sposób podpisu</div>
              <div className={styles.signOptions}>
                <div
                  className={`${styles.signOption} ${signMode === "analog" ? styles.selected : ""}`}
                  onClick={() => setSignMode("analog")}
                >
                  <div className={styles.optTitle}>Podpis tradycyjny</div>
                  <div className={styles.optHint}>
                    Pismo do wydruku i podpisu odręcznego. Pisma zostaną wygenerowane
                    od razu i poczekają w „Projektach pism” — po podpisaniu wydruku
                    kliknij „Przekaż do wysyłki”.
                  </div>
                </div>

                <div
                  className={`${styles.signOption} ${signMode === "own" ? styles.selected : ""}`}
                  onClick={() => setSignMode("own")}
                >
                  <div className={styles.optTitle}>Własny podpis elektroniczny</div>
                  <div className={styles.optHint}>
                    Wskaż certyfikat i ustaw pozycję znacznika na piśmie bazowym —
                    każda wygenerowana kopia zostanie podpisana i przekazana do
                    biura podawczego (do wysyłki).
                  </div>
                </div>

                <div
                  className={`${styles.signOption} ${signMode === "pass" ? styles.selected : ""}`}
                  onClick={() => setSignMode("pass")}
                >
                  <div className={styles.optTitle}>Przekaż do podpisu</div>
                  <div className={styles.optHint}>
                    Wskaż osobę, która podpisze dokument — trafi on na jej listę „Do podpisu".
                  </div>
                  {signMode === "pass" && (
                    <>
                      <SearchMemberInWorkspace
                        label="Osoba podpisująca"
                        onChoose={(w) => setSigner(w)}
                      />
                      <TextField
                        size="small"
                        label="Uwaga (opcjonalnie)"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        fullWidth
                      />
                    </>
                  )}
                </div>
              </div>
            </>
          )}

          {error && <div className={styles.error}>{error}</div>}
        </div>

        <div className={styles.footer}>
          {stepIndex > 0 ? (
            <HollowButton text="Wstecz" onClick={handleBack} />
          ) : (
            <span />
          )}
          <div className={styles.footerRight}>
            <HollowButton text="Anuluj" onClick={close} />
            {isLastStep ? (
              <Button text="Zakończ" onClick={finish} loading={isLoading} />
            ) : (
              <Button text="Dalej" onClick={handleNext} />
            )}
          </div>
        </div>
      </div>
    </div>

      {ownSignGen && (
        <OwnSignFlow
          documentId={doc!.DocumentId}
          documentDate={ownSignGen.DocumentDate}
          baseFileUri={prep?.BaseFileUri}
          baseFileName={prep?.BaseFileName}
          baseEncryptionKeyId={prep?.BaseEncryptionKeyId ?? doc?.EncryptionKeyId}
          gen={ownSignGen}
          onCancel={() => setOwnSignGen(null)}
          onSigned={finishOwnSign}
        />
      )}
    </>
  );
};

export default GenerateLettersModal;
